/**
 * Data access for the RAISE Curriculum feature.
 *
 * Talks to the Plus AppSync API via two operations:
 *   - listRaiseCurriculum(input: AWSJSON!)   read  (query)
 *   - manageRaiseCurriculum(input: AWSJSON!) write (mutation)
 *
 * Every call sends a single AWSJSON `input` = { action, data }. The specific
 * operation is carried in `action`; the resolver Lambda owns shape/validation
 * and authorization (writes require RAISE admin, enforced server-side).
 *
 * Reads are not cached — each read hits the resolver so the UI always reflects
 * current backend state.
 */

import { callPlusGraphQLDirect } from "src/utilities/AWSServices";

const READ_QUERY = `query listRaiseCurriculum($input: AWSJSON!) {
  listRaiseCurriculum(input: $input)
}`;

const WRITE_MUTATION = `mutation manageRaiseCurriculum($input: AWSJSON!) {
  manageRaiseCurriculum(input: $input)
}`;

/**
 * Plus AppSync returns an AWSJSON scalar serialized as a JSON string, and the
 * response VTL runs $util.toJson on a payload the Lambda already json.dumps'd,
 * so the value can come back double-encoded. Parse repeatedly until we reach a
 * non-string (object/array).
 */
const parseResult = (response, fieldName) => {
  let raw = response?.data ? response.data[fieldName] : null;
  if (raw == null) return null;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      break;
    }
  }
  return raw;
};

const runRead = async (action, data = {}) => {
  const response = await callPlusGraphQLDirect(READ_QUERY, {
    input: JSON.stringify({ action, data }),
  });
  return parseResult(response, "listRaiseCurriculum");
};

const runWrite = async (action, data = {}) => {
  const response = await callPlusGraphQLDirect(WRITE_MUTATION, {
    input: JSON.stringify({ action, data }),
  });
  return parseResult(response, "manageRaiseCurriculum");
};

/* ---------------- reads ----------------
 * No client-side caching: every read hits the resolver directly so the UI
 * always reflects the current backend state. */

// One call for the whole section (all 5 tabs) — used by the admin.
export const getRaisePage = () => runRead("GET_RAISE_PAGE");
export const getCurriculum = () => runRead("GET_CURRICULUM");
export const listCalendar = () => runRead("LIST_CALENDAR");
export const listCertifications = () => runRead("LIST_CERTIFICATIONS");
export const listFaq = () => runRead("LIST_FAQ");

/* ---------------- writes (admin) ----------------
 * Each returns the resolver result. Callers re-fetch after a write to pick up
 * the change (there is no cache to bust). */

export const upsertCourse = (data) => runWrite("UPSERT_COURSE", data);
export const deleteCourse = (courseId) => runWrite("DELETE_COURSE", { course_id: courseId });
// Set a matrix cell's tagline (by cell_id, or skill_set_id + tier_id which
// lazily creates the cell). Cells are never deleted from the UI.
export const upsertCell = (data) => runWrite("UPSERT_CELL", data);
export const upsertSession = (data) => runWrite("UPSERT_SESSION", data);
export const deleteSession = (sessionId) => runWrite("DELETE_SESSION", { session_id: sessionId });
export const upsertFaq = (data) => runWrite("UPSERT_FAQ", data);
export const deleteFaq = (faqId) => runWrite("DELETE_FAQ", { faq_id: faqId });
export const upsertFaqCategory = (data) => runWrite("UPSERT_FAQ_CATEGORY", data);
export const deleteFaqCategory = (categoryId) =>
  runWrite("DELETE_FAQ_CATEGORY", { category_id: categoryId });

/* ---- Lookup lists that feed the editor dropdowns ----
 * A delete is refused by the backend when the value is still referenced (it
 * returns an in-use error the UI surfaces); otherwise it is removed. */
export const upsertProvider = (data) => runWrite("UPSERT_PROVIDER", data);
export const deleteProvider = (providerId) =>
  runWrite("DELETE_PROVIDER", { provider_id: providerId });
export const upsertPlatform = (data) => runWrite("UPSERT_PLATFORM", data);
export const deletePlatform = (platformId) =>
  runWrite("DELETE_PLATFORM", { platform_id: platformId });
export const upsertSkillSet = (data) => runWrite("UPSERT_SKILL_SET", data);
export const deleteSkillSet = (skillSetId) =>
  runWrite("DELETE_SKILL_SET", { skill_set_id: skillSetId });
